import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { LabelKey } from '@/core/dcr/fields/labelKey';
import {
	getCollectionForGroup,
	resolveHistoryField
} from '@/core/dcr/fields/registry';
import { getSectionIndex, getSectionLabel } from '@/core/dcr/fields/sections';
import type { SectionId } from '@/core/dcr/fields/sections';
import type { FieldKind } from '@/core/dcr/fields/types';
import {
	Dcr,
	DcrEntityType,
	DcrFieldResult,
	DcrType,
	EntityType,
	Hcphco,
	ParentHco
} from '@/core/graphql/generated/types';
import { PRTN_RELATIONSHIP_TYPE_ID } from '@/pages/HcoProfilePage/sections/HcoAffiliationsSection/_constants/hcoAffiliationSections';

import { processEntityData } from '../../_utils/processEntityData';
import { useGetRelationshipTypeOptionsQuery } from '../_graphql/query/GetRelationshipTypeOptions.hook';

const RELATIONSHIP_TYPE_FIELDS = ['typeOfRelationship_V', 'relationshipType_V'];
const YES_NO_FIELDS = ['prescriberCda_V'];
const UNRESOLVED_ORDER = Number.MAX_SAFE_INTEGER;

export type ResultWithType = DcrFieldResult & {
	type: DcrEntityType;
	fieldLabel: string;
	fieldKind?: FieldKind;
};

export type TableRowType = ResultWithType | { label: string };

type Group = {
	label: string;
	sectionIndex: number;
	position: number;
	rows: Array<ResultWithType & { order: number }>;
};

const getAffiliations = (profile: Hcphco | null | undefined) => {
	if (!profile) return [];
	const search = profile.affiliationSearch;
	return (
		search?.__typename === 'HCPAffiliationsResults' ||
		search?.__typename === 'HCOAffiliationsResults'
			? (search.results ?? [])
			: []
	) as ParentHco[];
};

const indexByVid = (items: Array<{ vid_V?: string | null }>, vid: string) =>
	items.findIndex(item => item.vid_V === vid);

const isPartnerAffiliation = (
	group: string,
	vid: string,
	dcr: Dcr,
	profile: Hcphco | null,
	partnerRelationshipTypeName: string | undefined
) => {
	const relationshipTypeChange = dcr.fieldResults?.find(
		field =>
			field.group === group && RELATIONSHIP_TYPE_FIELDS.includes(field.field)
	);

	if (relationshipTypeChange) {
		const values = [
			relationshipTypeChange.requestedValue,
			relationshipTypeChange.finalValue,
			relationshipTypeChange.previousValue
		];

		return (
			values.includes(PRTN_RELATIONSHIP_TYPE_ID) ||
			(!!partnerRelationshipTypeName &&
				values.includes(partnerRelationshipTypeName))
		);
	}

	if (vid === 'new' || !profile) return false;
	const affiliation =
		getAffiliations(profile)[indexByVid(getAffiliations(profile), vid)];

	return affiliation?.relationshipType?.code === PRTN_RELATIONSHIP_TYPE_ID;
};

const getNewGroupIndex = (group: string) => Number(group.split(':')[2] ?? -1);

const getNewGroupOrdinals = (dcr: Dcr | null) => {
	const groupsByPrefix = new Map<string, Set<string>>();

	dcr?.fieldResults?.forEach(({ group }) => {
		const [prefix, vid] = group.split(':');
		if (vid !== 'new') return;

		const groups = groupsByPrefix.get(prefix) ?? new Set<string>();
		groups.add(group);
		groupsByPrefix.set(prefix, groups);
	});

	const ordinals: Record<string, number> = {};
	groupsByPrefix.forEach(groups => {
		[...groups]
			.sort((a, b) => getNewGroupIndex(a) - getNewGroupIndex(b))
			.forEach((group, i) => {
				ordinals[group] = i + 1;
			});
	});

	return ordinals;
};

const getProfileCountryId = (profile: Hcphco | null): string | undefined => {
	if (!profile) return undefined;

	const rawCountry =
		profile.countryId ??
		(profile as unknown as Record<string, unknown>).primaryCountry_V;

	return typeof rawCountry === 'string' ? rawCountry.toLowerCase() : undefined;
};

const mapValues = (
	field: DcrFieldResult,
	map: (value: string | null | undefined) => string | null | undefined
): DcrFieldResult => ({
	...field,
	requestedValue: map(field.requestedValue),
	previousValue: map(field.previousValue),
	finalValue: map(field.finalValue)
});

export const useDcr = (dcr: Dcr | null) => {
	const { t } = useTranslation();

	const entityType =
		dcr?.entityType === DcrEntityType.Hco ? EntityType.Hco : EntityType.Hcp;

	const profile = useMemo(
		() =>
			dcr?.type === DcrType.Change
				? dcr?.currentEntity || null
				: dcr?.entityData
					? processEntityData(dcr.entityData, dcr.entityType)
					: null,
		[dcr]
	);

	const countryId = useMemo(() => getProfileCountryId(profile), [profile]);

	const translate = useCallback(
		(label: LabelKey) => t(label.key, label.params ?? {}),
		[t]
	);

	const { data: relationshipTypeData } = useGetRelationshipTypeOptionsQuery({
		skip: !dcr || dcr.entityType !== DcrEntityType.Hco
	});

	const partnerRelationshipTypeName = useMemo(() => {
		const result = relationshipTypeData?.RELATIONSHIP_TYPE;
		if (result?.__typename !== 'ReferenceResult') return undefined;

		return result.results.find(
			option => option.id === PRTN_RELATIONSHIP_TYPE_ID
		)?.name;
	}, [relationshipTypeData]);

	const hcoParentGroups = useMemo(() => {
		const groups: Record<string, { label: string; isPartner: boolean }> = {};
		if (!dcr || dcr.entityType !== DcrEntityType.Hco) return groups;

		const currentEntity = dcr.currentEntity ?? null;
		const affiliations = getAffiliations(currentEntity);
		const seen = new Set<string>();

		const entries = (dcr.fieldResults ?? []).flatMap(field => {
			if (!field.group.startsWith('parentHcos_V:') || seen.has(field.group)) {
				return [];
			}
			seen.add(field.group);

			const [, vid, newIndexRaw] = field.group.split(':');
			return [
				{
					group: field.group,
					vid,
					newIndex: newIndexRaw !== undefined ? Number(newIndexRaw) : -1,
					isPartner: isPartnerAffiliation(
						field.group,
						vid,
						dcr,
						currentEntity,
						partnerRelationshipTypeName
					)
				}
			];
		});

		const existingOrder = (vid: string) => {
			const index = indexByVid(affiliations, vid);
			return index === -1 ? Infinity : index;
		};

		const counters = { partner: 0, other: 0 };

		[...entries]
			.sort((a, b) => {
				const aIsNew = a.vid === 'new';
				const bIsNew = b.vid === 'new';
				if (aIsNew !== bIsNew) return aIsNew ? 1 : -1;
				return aIsNew
					? a.newIndex - b.newIndex
					: existingOrder(a.vid) - existingOrder(b.vid);
			})
			.forEach(entry => {
				const counterKey = entry.isPartner ? 'partner' : 'other';
				counters[counterKey] += 1;
				const text = translate(
					getSectionLabel(entry.isPartner ? 'partners' : 'affiliations')
				);
				groups[entry.group] = {
					label: `${text} ${counters[counterKey]}`,
					isPartner: entry.isPartner
				};
			});

		return groups;
	}, [dcr, partnerRelationshipTypeName, translate]);

	const newGroupOrdinals = useMemo(() => getNewGroupOrdinals(dcr), [dcr]);

	const describeGroup = useCallback(
		(group: string, fieldSection: SectionId | undefined) => {
			const collection = getCollectionForGroup(entityType, group);

			if (!collection) {
				const section = fieldSection ?? 'name';
				return {
					label: fieldSection
						? translate(getSectionLabel(section))
						: t('Other'),
					sectionIndex: fieldSection
						? getSectionIndex(entityType, section)
						: Infinity,
					position: 0
				};
			}

			const vid = group.split(':')[1];
			const newIndex = newGroupOrdinals[group] ?? 1;
			const parentGroup = hcoParentGroups[group];
			const section: SectionId = parentGroup?.isPartner
				? 'partners'
				: collection.def.section;

			const currentEntity = dcr?.currentEntity ?? null;
			const existing =
				collection.key === 'addresses_V'
					? (currentEntity?.addresses ?? [])
					: collection.key === 'licenses_V'
						? (currentEntity?.licenses ?? [])
						: getAffiliations(currentEntity);

			const position =
				vid === 'new'
					? existing.length + newIndex
					: indexByVid(existing, vid) + 1;

			return {
				label:
					parentGroup?.label ??
					`${translate(collection.collection.itemLabel)} ${position || ''}`.trim(),
				sectionIndex: getSectionIndex(entityType, section),
				position
			};
		},
		[dcr, entityType, hcoParentGroups, newGroupOrdinals, t, translate]
	);

	const data: TableRowType[] = useMemo(() => {
		if (!dcr?.fieldResults) return [];

		const groups = new Map<string, Group>();

		dcr.fieldResults.forEach(result => {
			const resolved = resolveHistoryField(
				entityType,
				result.group,
				result.field,
				countryId
			);
			const groupInfo = describeGroup(result.group, resolved?.section);
			const groupKey = `${groupInfo.sectionIndex}:${groupInfo.label}`;

			const normalized = YES_NO_FIELDS.includes(result.field)
				? mapValues(result, value => {
						const upper = value?.trim().toUpperCase();
						return upper === 'Y' ? t('Yes') : upper === 'N' ? t('No') : value;
					})
				: result.field === 'countryCda_V'
					? mapValues(result, value =>
							value === 'Great Britain' ? 'United Kingdom' : value
						)
					: result;

			const group = groups.get(groupKey) ?? { ...groupInfo, rows: [] };
			group.rows.push({
				...normalized,
				type: dcr.entityType,
				fieldLabel: resolved ? translate(resolved.label) : result.field,
				fieldKind: resolved?.kind,
				order: resolved?.order ?? UNRESOLVED_ORDER
			});
			groups.set(groupKey, group);
		});

		return [...groups.values()]
			.sort(
				(a, b) =>
					a.sectionIndex - b.sectionIndex ||
					a.position - b.position ||
					a.label.localeCompare(b.label, undefined, { numeric: true })
			)
			.flatMap(group => [
				{ label: group.label },
				...[...group.rows]
					.sort((a, b) => a.order - b.order)
					.map(({ order: _order, ...row }) => row)
			]);
	}, [countryId, dcr, describeGroup, entityType, t, translate]);

	return {
		data,
		profile,
		countryId
	};
};
